import { z } from "zod";
import { Persistence } from "../../config/constants";
import { BaseEntity } from "../../core/entity/BaseEntity";
import { Entity } from "../../core/entity/Entity";
import { materialSchema } from "../../game/gameStateSchema";
import { MaterialInstance } from "../../game/Materials";
import {
  registerSerializable,
  SerializableEntity,
} from "../save/serialization";

/**
 * The for-sale stand, as a sim entity — the old world's `GameState.stand`
 * slice. The table itself is lot geometry (its solid comes from
 * `standSolid` in the shared collision assembly, see `src/sim/collision.ts`);
 * this entity owns whether the table is out yet and the pieces set out
 * on it, oldest first.
 *
 * A new shop has no table: the end of the driveway is grass until the
 * story offers the spot and the player sets the table up there by hand
 * (`setUpStand`). Only a set-up stand is solid, draws, or takes work.
 *
 * The stand-commands set the table up, set pieces out and take them
 * back; the StreetSystem sells them off. The model — geometry, reach,
 * what it accepts, what a sale pays — stays in the shared
 * `src/game/stand.ts`.
 */

const schema = z.object({
  setUp: z.boolean(),
  pieces: z.array(materialSchema),
});

export class StandEntity
  extends BaseEntity
  implements Entity, SerializableEntity
{
  readonly saveType = "stand";
  id = "stand";
  persistenceLevel: number = Persistence.Game;

  /** Whether the table is out by the road. */
  setUp: boolean;
  /** The pieces set out on the table, oldest first. */
  pieces: MaterialInstance[];

  constructor({
    setUp = false,
    pieces = [],
  }: {
    setUp?: boolean;
    pieces?: ReadonlyArray<MaterialInstance>;
  } = {}) {
    super();
    this.setUp = setUp;
    this.pieces = [...pieces];
  }

  toJSON(): { setUp: boolean; pieces: MaterialInstance[] } {
    return {
      setUp: this.setUp,
      pieces: this.pieces.map((piece) => ({ ...piece })),
    };
  }
}

registerSerializable({
  type: "stand",
  singleton: true,
  schema,
  fromJSON: (data) =>
    new StandEntity({
      setUp: data.setUp,
      pieces: data.pieces as unknown as MaterialInstance[],
    }),
});
